import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, output } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { RouterLink } from "@angular/router";
import {
    ButtonComponent,
    ButtonVariant,
    InputOutline,
    TextareaComponent,
    ToggleComponent
} from "@sxm-content-creators/tools-ds";
import { catchError, map, of } from "rxjs";
import { TagListComponent } from "../../../../components/tag/tag-list.component";
import { TagOption } from "../../../../components/tag/tag.component";
import { SnackbarService } from "../../../../components/snackbar/snackbar.service";
import { hasEntityPrefix, stripPrefix } from "../../../../shared/models/entity.model";
import { ThemesApiService } from "../../../themes/services/themes.api.service";
import {
    MAX_SEARCH_KEYWORDS,
    TENANT_DESCRIPTION_MAX_LENGTH,
    TENANT_ENTITY_PREFIXES,
    TenantFormValue,
    TenantResponse
} from "../../models/tenant.model";

export type TenantFormMode = "create" | "edit";

/**
 * Rejects anything that isn't a show or channel id, so the free-text field can only produce the
 * `EntityId` the payload is typed as. Without this an unprefixed id saves fine and then throws
 * downstream, where the backend's `EntityIdHelper.getType` rejects it while publishing interactives.
 * An interactive's own prefix (`IP:`/`IF:`) is refused too — the backend would take it, but a
 * tenant is a show or a channel, so it would only ever be a typo.
 * Blank always passes — the field is optional, including on a tenant that already has an id:
 * clearing it is a valid way to disconnect the tenant from its catalog entity.
 */
function entityIdValidator(control: AbstractControl<string>): ValidationErrors | null {
    const value = control.value.trim();
    if (!value) return null;
    // A bare prefix (`SH:`) satisfies the type but isn't an id, so require something after it.
    return hasEntityPrefix(value, TENANT_ENTITY_PREFIXES) && stripPrefix(value) ? null : { entityIdFormat: true };
}

/**
 * Create/edit form for a tenant, shared by both tenant routes. Owns every field the
 * create/update payload carries (name, description, theme, entity id, call-in override,
 * search keywords) and emits them trimmed on submit; the pages own the request itself.
 */
@Component({
    selector: "app-tenant-form",
    templateUrl: "./tenant-form.component.html",
    styleUrl: "./tenant-form.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        ReactiveFormsModule,
        RouterLink,
        MatFormFieldModule,
        MatSelectModule,
        InputOutline,
        TextareaComponent,
        ToggleComponent,
        ButtonComponent,
        TagListComponent
    ]
})
export class TenantFormComponent implements OnInit {
    private readonly formBuilder = inject(FormBuilder);
    private readonly themesService = inject(ThemesApiService);
    private readonly snackbarService = inject(SnackbarService);

    readonly mode = input<TenantFormMode>("create");
    /** Existing tenant to pre-fill when editing. */
    readonly initialTenant = input<TenantResponse | null>(null);
    /** Disables the primary action while a create/update request is in flight. */
    readonly isSubmitting = input(false);
    /** Emits the tenant form value when the primary is triggered. */
    readonly formSubmit = output<TenantFormValue>();

    readonly form = this.formBuilder.nonNullable.group({
        // `required` alone accepts "   ", so pair it with a non-whitespace pattern.
        name: this.formBuilder.nonNullable.control("", [Validators.required, Validators.pattern(/\S/)]),
        description: this.formBuilder.nonNullable.control("", Validators.maxLength(TENANT_DESCRIPTION_MAX_LENGTH)),
        // The backend requires a theme (`@NotNull themeId`), so this can't be submitted empty.
        themeId: this.formBuilder.control<string | null>(null, Validators.required),
        // Optional for both create and edit — a tenant can go without a catalog entity, and an
        // existing one can be cleared. Whatever is entered has to carry a known entity prefix.
        entityId: this.formBuilder.nonNullable.control("", entityIdValidator),
        // Held as tags so app-tag-list can render/remove them; mapped back to strings on submit.
        searchAliases: this.formBuilder.nonNullable.control<TagOption<string>[]>([]),
        prioritizeOverCallIn: this.formBuilder.nonNullable.control(false)
    });

    /** The keyword being typed. Not part of the payload — Enter moves it into `searchAliases`. */
    readonly keyword = this.formBuilder.nonNullable.control("");

    readonly themes = toSignal(
        this.themesService.getAllThemes().pipe(
            map(response => [...response.items].sort((a, b) => a.name.localeCompare(b.name))),
            // Say so rather than rendering an empty dropdown that does nothing when clicked.
            catchError(() => {
                this.snackbarService.show("We couldn’t load the themes. Try again.", "error");
                return of([]);
            })
        ),
        { initialValue: [] }
    );

    private readonly formEvents = toSignal(this.form.events, { initialValue: null });
    private readonly searchAliases = toSignal(this.form.controls.searchAliases.valueChanges, {
        initialValue: this.form.controls.searchAliases.value
    });

    // Re-read validity whenever the form emits (value/status/touched), since `form.invalid`
    // isn't reactive on its own.
    readonly isSubmitDisabled = computed(() => {
        this.formEvents();
        return this.form.invalid || this.isSubmitting();
    });

    readonly hasKeywords = computed(() => this.searchAliases().length > 0);
    readonly isKeywordLimitReached = computed(() => this.searchAliases().length >= MAX_SEARCH_KEYWORDS);

    /** What's in the field isn't a prefixed entity id. */
    readonly entityIdFormatError = computed(() => {
        this.formEvents();
        const entityId = this.form.controls.entityId;
        return entityId.touched && entityId.hasError("entityIdFormat");
    });

    readonly title = computed(() => (this.mode() === "edit" ? "Edit Tenant" : "Create New Tenant"));
    readonly primaryLabel = computed(() => (this.mode() === "edit" ? "Save" : "Create Tenant"));

    ngOnInit(): void {
        const tenant = this.initialTenant();
        if (!tenant) return;

        this.form.patchValue({
            name: tenant.name,
            description: tenant.description ?? "",
            themeId: tenant.themeId,
            entityId: tenant.entityId ?? "",
            searchAliases: (tenant.searchAliases ?? []).map(keyword => this.toTag(keyword)),
            prioritizeOverCallIn: tenant.prioritizeOverCallIn
        });
    }

    /** Adds the typed keyword as a tag, ignoring blanks, duplicates, and anything past the max. */
    addKeyword(event?: Event): void {
        // Enter is the add gesture here, not a form submit.
        event?.preventDefault();

        const keyword = this.keyword.value.trim();
        if (!keyword || this.isKeywordLimitReached()) return;

        const existing = this.form.controls.searchAliases.value;
        if (!existing.some(tag => tag.value.toLowerCase() === keyword.toLowerCase())) {
            this.form.controls.searchAliases.setValue([...existing, this.toTag(keyword)]);
        }
        this.keyword.setValue("");
    }

    onSubmit(): void {
        if (this.isSubmitDisabled()) return;
        this.formSubmit.emit(this.buildValue());
    }

    private buildValue(): TenantFormValue {
        const { name, description, themeId, entityId, searchAliases, prioritizeOverCallIn } = this.form.getRawValue();
        // Explicit null (not "") when blank: the backend rejects a present-but-blank entityId, but
        // treats null as a deliberate unset — the only way to clear an existing one. The guard
        // doubles as the narrowing from the free-text control to `EntityId`; anything malformed has
        // already been stopped by `entityIdValidator`, which keeps the form invalid.
        const trimmedEntityId = entityId.trim();
        return {
            // Non-null once the form is valid (themeId is required).
            themeId: themeId ?? "",
            entityId: hasEntityPrefix(trimmedEntityId, TENANT_ENTITY_PREFIXES) ? trimmedEntityId : null,
            name: name.trim(),
            description: description.trim(),
            prioritizeOverCallIn,
            searchAliases: searchAliases.map(tag => tag.value)
        };
    }

    private toTag(keyword: string): TagOption<string> {
        return { value: keyword, label: keyword };
    }

    protected readonly descriptionMaxLength = TENANT_DESCRIPTION_MAX_LENGTH;
    protected readonly maxKeywords = MAX_SEARCH_KEYWORDS;
    protected readonly ButtonVariant = ButtonVariant;
}
